/* CWR 2.2 — Book a Session catalog page. CSS copied verbatim from the confirmed
   mockup (docs/mockups/client-dashboard-redesign/book-session-catalog.html); the
   only deltas are the approved token swaps:
     · raw rgba(var(--accent-light-rgb),…) card/tab rings → --catalog-card-ring-bg / --catalog-tab-ring-bg
     · raw var(--accent-hot) (event date / low spots / free price) → --event-date-color / --spots-low-color / --price-free-color
     · var(--font-display) → var(--client-font-heading), the theme's heading font (matches ClientShell)
     · --r-md (14px) → literal 14px
   Cards are NON-INTERACTIVE <article>s in 2.2 (browse-only until 2.4); the CTA is
   a presentational label, never a focusable no-op control. */

/* ── Header (rendered into the ClientShell header slot) ── */
.pageHeaderSection {
  position: relative;
  padding: 0 18px 12px;
}
.pageBackBtn {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(var(--accent-rgb), 0.3);
  position: absolute;
  top: 2px;
  left: 18px;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  color: var(--chevron-color);
  display: grid;
  place-items: center;
}
.pageBackBtn svg {
  width: 19px;
  height: 19px;
  stroke-width: 2.6;
}
.pageTitleBlock {
  text-align: center;
  padding: 0 64px; /* clears the larger (44px) back button */
}
.pageTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 25px;
  letter-spacing: -0.006em;
  line-height: 1.1;
  margin-bottom: 3px;
  color: var(--text-primary);
}
.pageSubtitle {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.35;
}

/* ── Segmented Services / Events tabs ── */
.tabSegmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;
  padding: 3px;
  margin: 0 18px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  position: relative;
  overflow: hidden;
}
.tabSegmented::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--catalog-tab-ring-bg);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.tabSegment {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
  position: relative;
  z-index: 1;
  padding: 9px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-align: center;
}
.tabSegment.active {
  color: var(--text-primary);
  background: rgba(var(--accent-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.45), 0 0 18px rgba(var(--accent-rgb), 0.18);
}

/* ── Card list ── */
.servicesContent { padding: 14px 18px 0; }
.servicesContent > .servicesList { padding: 0; }
/* Paused notice uses the same glass surface and ring as the catalog cards. */
.closed {
  position: relative;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  align-items: center;
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
}
.closedIcon { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; background: rgba(var(--accent-rgb), 0.16); color: var(--accent); }
.closedIcon svg { width: 16px; height: 16px; stroke-width: 2.2; }
.closedTitle { font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif); font-size: 16px; font-weight: 600; color: var(--text-primary); letter-spacing: -0.005em; }
.closedSubtitle { font-size: 12px; color: var(--text-secondary); margin-top: 1px; }

.servicesList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 0;
}
.serviceCard {
  position: relative;
  display: grid;
  grid-template-columns: 130px 1fr;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  overflow: hidden;
}
.serviceCard::before,
.closed::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--catalog-card-ring-bg);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 3;
}
.serviceImage {
  position: relative;
  background-size: cover;
  background-position: center;
}
/* image-edge vignette (mockup literals; theme-neutral warm darkening) */
.serviceImage::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 82%, rgba(20, 14, 10, 0.22) 100%),
    linear-gradient(180deg, transparent 70%, rgba(20, 14, 10, 0.4) 100%);
}
.serviceContent {
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  gap: 6px;
  min-width: 0;
}
.serviceTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 21px;
  letter-spacing: -0.006em;
  line-height: 1.08;
  color: var(--text-primary);
}
.serviceDesc {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* The facts row wraps when a long price does not fit (a "from" price clipped at
   390 wide, service-card-from-price-clip). Every part carries its divider in
   front; the line is pulled left by one divider and its gap, so the divider
   that starts each line sits outside .serviceMeta and is clipped. */
.serviceMeta {
  margin-top: 2px;
  overflow: hidden;
}
.metaLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 7px;
  row-gap: 4px;
  margin-left: -8px;
}
.metaPart {
  display: flex;
  align-items: center;
  gap: 7px;
}
.serviceMetaItem {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-secondary);
  flex: 0 0 auto;
  white-space: nowrap;
}
.serviceMetaItem svg {
  width: 12px;
  height: 12px;
  color: var(--accent);
  stroke-width: 1.8;
}
.serviceMetaDivider {
  width: 1px;
  height: 11px;
  background: var(--border-soft);
}
.serviceMetaPrice {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  letter-spacing: -0.01em;
  flex: 0 0 auto;
  white-space: nowrap;
}
.serviceMetaPrice .from {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-tertiary);
  margin-right: 2px;
}
.serviceMetaPrice.free {
  color: var(--price-free-color);
}
/* CTA — presentational label in 2.2 (non-interactive). */
.serviceCta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(var(--accent-rgb), 0.55);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--accent);
}
.serviceCta svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}
.ctaBooked {
  border-color: transparent;
  background: rgba(var(--accent-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.45);
  color: var(--accent-hot);
  font-weight: 600;
}
.ctaStatus,
.ctaView {
  display: inline-flex;
  align-items: center;
}
.ctaStatus {
  gap: 5px;
}
.ctaStatus svg {
  width: 12px;
  height: 12px;
  stroke-width: 2.6;
}
.ctaView {
  gap: 4px;
  color: var(--accent);
  font-weight: 500;
}
.ctaView svg {
  width: 13px;
  height: 13px;
}
.modality {
  color: var(--text-secondary);
}
.modality svg {
  color: var(--accent);
}
/* ── Event card extras ── */
.eventCard .serviceDesc {
  -webkit-line-clamp: 2;
}
.eventCard .metaLine {
  column-gap: 5px;
  margin-left: -6px;
}
.eventCard .metaPart {
  gap: 5px;
}
.eventDate {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--event-date-color);
}
.eventDate svg {
  width: 13px;
  height: 13px;
  stroke-width: 1.9;
}
.spotsLow {
  color: var(--spots-low-color);
  font-weight: 500;
}
.spotsOut {
  color: var(--text-tertiary);
}

/* ── Load more (mock-not-shown; design-system styled) ── */
.loadMore {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  display: block;
  margin: 14px auto 4px;
  padding: 9px 22px;
  border-radius: 9999px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--accent);
}
.loadMore:disabled {
  color: var(--text-tertiary);
}

/* ── States ── */
.stateBlock {
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}
.retryBtn {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  margin-top: 12px;
  padding: 9px 20px;
  border-radius: 10px;
  border: 1px solid rgba(var(--accent-rgb), 0.55);
  color: var(--accent);
  font-size: 13px;
  font-weight: 500;
}
.skeletonCard {
  height: 116px;
  border-radius: 14px;
  background: var(--glass-bg);
  box-shadow: var(--glass-shadow);
  opacity: 0.5;
}
